import { PreguntaUsuario, RegistroEvaluacion } from '@/app/models';
import { environment } from '@/environments/environment';
import * as pdfMake from 'pdfmake';
import {
  Content,
  DynamicContent,
  Table,
  TDocumentDefinitions,
} from 'pdfmake/interfaces';
import { checkbox, checkboxCorrect, checkboxIncorrect } from './checkbox-svg';
import { formatDate } from './format-date';

function generarPreguntas(preguntasUsuario: Partial<PreguntaUsuario>[]) {
  const table: Table = {
    widths: ['50%', '50%'],
    body: [],
  };

  let filaActual: Content[] = [];

  preguntasUsuario.forEach((preguntaUsuario, index) => {
    const { pregunta, respuestaUsuario } = preguntaUsuario;
    const preguntaText: Content = {
      text: `${index + 1}. ${pregunta?.pregunta}`,
      bold: true,
      margin: [0, 10, 0, 5],
    };

    const respuestasText: Content[] = (respuestaUsuario ?? []).flatMap(
      (respuestaUsuario) => {
        const fillColor =
          respuestaUsuario.respuestaOriginal?.correcta &&
          respuestaUsuario.seleccionada
            ? '#26a907'
            : !respuestaUsuario.respuestaOriginal?.correcta &&
                respuestaUsuario.seleccionada
              ? '#900c23'
              : respuestaUsuario.respuestaOriginal?.correcta &&
                  !respuestaUsuario.seleccionada
                ? '#dc7a03'
                : '';
        const color =
          respuestaUsuario.respuestaOriginal?.correcta &&
          respuestaUsuario.seleccionada
            ? '#fff'
            : !respuestaUsuario.respuestaOriginal?.correcta &&
                respuestaUsuario.seleccionada
              ? '#fff'
              : respuestaUsuario.respuestaOriginal?.correcta &&
                  !respuestaUsuario.seleccionada
                ? '#fff'
                : '';
        const bold =
          respuestaUsuario.respuestaOriginal?.correcta &&
          respuestaUsuario.seleccionada
            ? true
            : !respuestaUsuario.respuestaOriginal?.correcta &&
                respuestaUsuario.seleccionada
              ? true
              : respuestaUsuario.respuestaOriginal?.correcta &&
                  !respuestaUsuario.seleccionada
                ? true
                : false;

        const svg =
          respuestaUsuario.respuestaOriginal?.correcta &&
          respuestaUsuario.seleccionada
            ? checkboxCorrect
            : !respuestaUsuario.respuestaOriginal?.correcta &&
                respuestaUsuario.seleccionada
              ? checkboxIncorrect
              : respuestaUsuario.respuestaOriginal?.correcta &&
                  !respuestaUsuario.seleccionada
                ? checkboxIncorrect
                : checkbox;

        return [
          {
            table: {
              widths: [8, '*'],
              body: [
                [
                  {
                    svg,
                    width: 15,
                    fillColor,
                  },
                  {
                    text: respuestaUsuario?.respuestaOriginal?.respuesta || '',
                    fillColor,
                    color,
                    bold,
                    width: '*',
                  },
                ],
              ],
            },
            layout: 'noBorders',
          } as Content,
          { text: '', margin: [0, 1, 0, 1] }, // 🔹 Agrega espacio entre filas
        ];
      },
    );

    const espacioBetween: Content = { text: '', margin: [0, 0, 0, 5] };

    const bloquePregunta = [preguntaText, ...respuestasText, espacioBetween];

    filaActual.push({ stack: bloquePregunta });

    if (filaActual.length === 2) {
      table.body.push(filaActual);
      filaActual = []; // Reiniciamos la fila actual
    }
  });

  // Si quedó una pregunta sin pareja (caso impar), agregamos una celda vacía para que complete la fila
  if (filaActual.length === 1) {
    filaActual.push({ text: '' }); // Segunda celda vacía
    table.body.push(filaActual);
  }

  return table;
}

export function generateUserPdf(registroEvaluacion: RegistroEvaluacion) {
  const { evaluacion, preguntaUsuario, usuario } = registroEvaluacion;

  const pM = (pdfMake as any).default || pdfMake;

  pM.fonts = {
    Roboto: {
      normal: environment.webUrl + 'fonts/urbanist-regular.ttf',
      bold: environment.webUrl + 'fonts/urbanist-bold.ttf',
      italics: environment.webUrl + 'fonts/urbanist-italic.ttf',
      bolditalics: environment.webUrl + 'fonts/urbanist-bold.ttf',
    },
  };

  const table = generarPreguntas(preguntaUsuario);

  const footer: DynamicContent | Content | undefined = (
    currentPage: number,
    pageCount: number,
  ) => {
    return {
      text: `Página ${currentPage} de ${pageCount}`,
      alignment: 'right',
      margin: [15, 0],
      fontSize: 9,
    };
  };

  const d: TDocumentDefinitions = {
    content: [
      {
        text: evaluacion.planilla?.titulo ?? 'Sin planilla',
        style: 'header',
        alignment: 'center',
      },
      {
        text: evaluacion.planilla?.descripcion ?? 'Sin planilla',
        margin: [0, 0, 0, 10],
      },
      {
        text: `${evaluacion.capacitacion?.nombre ?? 'No asignado'}`,
        bold: true,
        margin: [0, 0, 0, 5],
        alignment: 'center',
      },
      {
        table: {
          widths: ['auto', '*'], // Primera columna tamaño automático, segunda ocupa el resto
          body: [
            [
              {
                text: [
                  {
                    text: 'Descripción: ',
                    bold: true,
                  },
                  {
                    text:
                      evaluacion.capacitacion?.descripcion ?? 'Sin descripción',
                  },
                ],
                alignment: 'left',
              },
              {
                text: [
                  { text: 'Vigencia: ', bold: true },
                  {
                    text: `${
                      evaluacion.capacitacion?.vigencia ?? '(No asignado)'
                    } meses`,
                  },
                ],
                alignment: 'right',
              },
            ],
            [
              {
                text: [
                  { text: 'Nota mínima para la aprobación: ', bold: true },
                  {
                    text:
                      evaluacion.capacitacion?.nota_aprobacion ?? 'No definido',
                  },
                ],
                alignment: 'left',
              },
              {
                text: '',
                alignment: 'right',
              },
            ],
          ],
        },
        layout: 'noBorders', // Para quitar los bordes de la tabla
        margin: [0, 0],
      },
      { text: 'Preguntas:', style: 'subheader' },
      { table, layout: 'noBorders' },
      {
        margin: [0, 30],
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              {
                text: 'Nombre y apellido:',
                bold: true,
                margin: [0, 5],
                fontSize: 9,
              },
              {
                colSpan: 3,
                table: {
                  widths: ['100%'],
                  body: [
                    [
                      {
                        text: `${usuario.nombre} ${usuario.apellido}`,
                        border: [false, false, false, true],
                      },
                    ],
                  ],
                },
                layout: {
                  hLineStyle: function () {
                    // Solo aplica al borde bottom
                    return { dash: { length: 2, space: 2 } };
                  },
                  hLineWidth: function () {
                    return 0.5; // Ancho de la línea punteada
                  },
                  vLineWidth: function () {
                    return 0; // Sin bordes verticales
                  },
                },
              },
              { text: '' },
              { text: '' },
            ],
            [
              {
                text: 'CUIL:',
                bold: true,
                margin: [0, 5],
                fontSize: 9,
              },
              {
                colSpan: 3,
                table: {
                  widths: ['100%'],
                  body: [
                    [
                      {
                        text: usuario.cuil,
                        border: [false, false, false, true],
                      },
                    ],
                  ],
                },
                layout: {
                  hLineStyle: function () {
                    // Solo aplica al borde bottom
                    return { dash: { length: 2, space: 2 } };
                  },
                  hLineWidth: function () {
                    return 0.5; // Ancho de la línea punteada
                  },
                  vLineWidth: function () {
                    return 0; // Sin bordes verticales
                  },
                },
              },
              { text: '' },
              { text: '' },
            ],
            [
              {
                text: 'Fecha:',
                bold: true,
                margin: [0, 5],
                fontSize: 9,
              },
              {
                table: {
                  widths: ['100%'],
                  body: [
                    [
                      {
                        text: formatDate(registroEvaluacion.fecha_carga) ?? '',
                        border: [false, false, false, true],
                      },
                    ],
                  ],
                },
                layout: {
                  hLineStyle: function () {
                    // Solo aplica al borde bottom
                    return { dash: { length: 2, space: 2 } };
                  },
                  hLineWidth: function () {
                    return 0.5; // Ancho de la línea punteada
                  },
                  vLineWidth: function () {
                    return 0; // Sin bordes verticales
                  },
                },
              },
              {
                text: 'Calificación:',
                bold: true,
                margin: [0, 5],
                fontSize: 9,
              },
              {
                table: {
                  widths: ['100%'],
                  body: [
                    [
                      {
                        text: registroEvaluacion.calificacion,
                        border: [false, false, false, true],
                      },
                    ],
                  ],
                },
                layout: {
                  hLineStyle: function () {
                    // Solo aplica al borde bottom
                    return { dash: { length: 2, space: 2 } };
                  },
                  hLineWidth: function () {
                    return 0.5; // Ancho de la línea punteada
                  },
                  vLineWidth: function () {
                    return 0; // Sin bordes verticales
                  },
                },
              },
            ],
          ],
        },
        layout: 'noBorders',
      },
    ],
    styles: {
      header: {
        fontSize: 16,
        bold: true,
        margin: [0, 0, 0, 10],
      },
      subheader: {
        fontSize: 14,
        bold: true,
        margin: [0, 10, 0, 5],
      },
    },
    footer,
  };

  // Generar PDF y abrir en navegador o guardarlo
  pM.createPdf(d).open();
}
